import {useCallback, useRef, useState} from 'react'

const MIN_ZOOM = 0.1
const MAX_ZOOM = 5.0
const ZOOM_STEP = 0.1
const WHEEL_ZOOM_FACTOR = 0.001

/**
 * Manages zoom level and pan offset for the remove-bg canvas.
 * Supports wheel zoom (toward cursor), button zoom, and hand-tool dragging.
 */
export function useRemoveBgZoomPan({containerRef}) {
    const [zoom, setZoom] = useState(1)
    const [pan, setPan] = useState({x: 0, y: 0})
    const isPanningRef = useRef(false)
    const lastMouseRef = useRef({x: 0, y: 0})

    const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))

    const zoomIn = useCallback(() => {
        setZoom((z) => clampZoom(z + ZOOM_STEP))
    }, [])

    const zoomOut = useCallback(() => {
        setZoom((z) => clampZoom(z - ZOOM_STEP))
    }, [])

    const zoomTo = useCallback((level) => {
        setZoom(clampZoom(level))
    }, [])

    const resetView = useCallback(() => {
        setZoom(1)
        setPan({x: 0, y: 0})
    }, [])

    /**
     * Fit the image to fill the container with padding, centered.
     * Called after image is loaded so the user immediately sees the full image.
     */
    const fitToView = useCallback(
        (imgWidth, imgHeight) => {
            const container = containerRef?.current
            if (!container || !imgWidth || !imgHeight) return

            const PADDING = 32
            const cw = container.clientWidth
            const ch = container.clientHeight
            const fitZoom = clampZoom(Math.min((cw - PADDING * 2) / imgWidth, (ch - PADDING * 2) / imgHeight))

            setZoom(fitZoom)
            setPan({
                x: (cw - imgWidth * fitZoom) / 2,
                y: (ch - imgHeight * fitZoom) / 2,
            })
        },
        [containerRef],
    )

    /**
     * Zoom toward mouse cursor position on wheel event.
     */
    const handleWheel = useCallback(
        (e) => {
            e.preventDefault()
            if (!e.ctrlKey && !e.metaKey) return
            const container = containerRef?.current
            if (!container) return

            const rect = container.getBoundingClientRect()
            const mouseX = e.clientX - rect.left
            const mouseY = e.clientY - rect.top

            setZoom((prevZoom) => {
                const delta = -e.deltaY * WHEEL_ZOOM_FACTOR
                const newZoom = clampZoom(prevZoom + delta * prevZoom)
                const ratio = newZoom / prevZoom

                setPan((prevPan) => ({
                    x: mouseX - ratio * (mouseX - prevPan.x),
                    y: mouseY - ratio * (mouseY - prevPan.y),
                }))

                return newZoom
            })
        },
        [containerRef],
    )

    const startPan = useCallback((e) => {
        isPanningRef.current = true
        lastMouseRef.current = {x: e.clientX, y: e.clientY}
    }, [])

    const movePan = useCallback((e) => {
        if (!isPanningRef.current) return
        const dx = e.clientX - lastMouseRef.current.x
        const dy = e.clientY - lastMouseRef.current.y
        lastMouseRef.current = {x: e.clientX, y: e.clientY}
        setPan((prev) => ({x: prev.x + dx, y: prev.y + dy}))
    }, [])

    const endPan = useCallback(() => {
        isPanningRef.current = false
    }, [])

    const zoomPercent = Math.round(zoom * 100)

    return {
        zoom,
        pan,
        zoomPercent,
        zoomIn,
        zoomOut,
        zoomTo,
        resetView,
        fitToView,
        handleWheel,
        startPan,
        movePan,
        endPan,
        isPanning: isPanningRef,
    }
}
